// src/components/Pagination.tsx

import React from "react";

const getPaginationRange = (currentPage: number, totalPages: number, maxButtons = 7) => {
    if (totalPages <= maxButtons) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const range: (number | string)[] = [];
    const siblings = Math.floor((maxButtons - 3) / 2);
    const leftSiblingIndex = Math.max(currentPage - siblings, 2);
    const rightSiblingIndex = Math.min(currentPage + siblings, totalPages - 1);

    range.push(1);

    if (leftSiblingIndex > 2) {
        range.push("...");
    }

    for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
        range.push(i);
    }

    if (rightSiblingIndex < totalPages - 1) {
        range.push("...");
    }

    range.push(totalPages);

    return range;
};


interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}) => {
  const paginationRange = getPaginationRange(currentPage, totalPages);

  return (
    <>
      <div className="mt-6 flex justify-center items-center gap-2 select-none">
        <button
          className={`px-4 py-2 rounded-md border border-gray-300 ${
            currentPage === 1 ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        {paginationRange.map((page, index) =>
          page === "..." ? (
            <span key={`ellipsis-${index}`} className="px-3 select-none">
              ...
            </span>
          ) : (
            <button
              key={page}
              className={`px-4 py-2 rounded-md border ${
                currentPage === page
                  ? "bg-[#8051FF] text-white border-[#8051FF]"
                  : "border-gray-300 text-gray-700"
              }`}
              onClick={() => onPageChange(Number(page))}
            >
              {page}
            </button>
          )
        )}

        <button
          className={`px-4 py-2 rounded-md border border-gray-300 ${
            currentPage === totalPages ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      <div className="mt-2 text-center text-gray-600 text-sm">
        Showing {(currentPage - 1) * pageSize + 1} -{" "}
        {Math.min(currentPage * pageSize, totalItems)} of {totalItems}
      </div>
    </>
  );
};

export default Pagination;
